import { useState, useEffect, useCallback } from 'react';
import { 
  generateNarrative, 
  getRoundTheme, 
  getRoundNarrator,
  getTeamLore,
  EventType,
  NarrativeResponse,
  RoundTheme,
  Narrator,
  Beast
} from '../services/narrative';

export interface UseNarrativeOptions {
  roundId: number;
  autoFetchTheme?: boolean;
  autoFetchNarrator?: boolean;
}

export interface UseNarrativeReturn {
  // Current round info
  theme: RoundTheme | null;
  narrator: Narrator | null;
  
  // Generate narrative for events
  narrate: (eventType: EventType, data: Record<string, any>) => Promise<NarrativeResponse>;
  
  // Last generated narrative
  lastNarrative: NarrativeResponse | null;
  
  // Loading states
  loading: boolean;
  
  // Lore
  getTeamBeast: (teamId: number) => Promise<Beast | null>;
  
  // Refresh
  refreshTheme: () => Promise<void>;
  refreshNarrator: () => Promise<void>;
}

/**
 * Hook for using the narrative engine in React components
 */
export function useNarrative(options: UseNarrativeOptions): UseNarrativeReturn {
  const { roundId, autoFetchTheme = true, autoFetchNarrator = true } = options;
  
  const [theme, setTheme] = useState<RoundTheme | null>(null);
  const [narrator, setNarrator] = useState<Narrator | null>(null);
  const [lastNarrative, setLastNarrative] = useState<NarrativeResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch theme for this round
  const refreshTheme = useCallback(async () => {
    const fetchedTheme = await getRoundTheme(roundId);
    setTheme(fetchedTheme);
  }, [roundId]);

  // Fetch narrator for this round
  const refreshNarrator = useCallback(async () => {
    const fetchedNarrator = await getRoundNarrator(roundId);
    setNarrator(fetchedNarrator);
  }, [roundId]);

  // Auto-fetch on mount or round change
  useEffect(() => {
    if (autoFetchTheme) refreshTheme();
    if (autoFetchNarrator) refreshNarrator();
  }, [roundId, autoFetchTheme, autoFetchNarrator, refreshTheme, refreshNarrator]);

  // Generate narrative for an event
  const narrate = useCallback(async (
    eventType: EventType, 
    data: Record<string, any>
  ): Promise<NarrativeResponse> => {
    setLoading(true);
    try {
      const response = await generateNarrative({
        eventType,
        roundId,
        data,
      });
      setLastNarrative(response);
      return response;
    } finally {
      setLoading(false);
    }
  }, [roundId]);

  // Get lore for a team
  const getTeamBeast = useCallback(async (teamId: number): Promise<Beast | null> => {
    return getTeamLore(teamId);
  }, []);

  return {
    theme,
    narrator,
    narrate,
    lastNarrative,
    loading,
    getTeamBeast,
    refreshTheme,
    refreshNarrator,
  };
}
